import { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { getPendingCases } from 'services/ScaleService'

/**
 * Custom hook to fetch and manage CAR Cases badge count
 * Fetches pending cases (awaiting_mediator_approval) and returns count with loading state
 * @returns {Object} { count: number, loading: boolean, refresh: function }
 */
const useCarCasesBadge = () => {
    const user = useSelector((state) => state.auth.user)
    const [count, setCount] = useState(0)
    const [loading, setLoading] = useState(true)

    const mediatorExternalId = user?.mediator_id || user?.external_id

    const fetchBadgeCount = useCallback(async () => {
        // If no mediator ID, set loading to false immediately
        if (!mediatorExternalId) {
            setCount(0)
            setLoading(false)
            return
        }

        try {
            setLoading(true)
            
            // Add a timeout to prevent hanging (30 seconds for slow networks)
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Request timeout')), 30000) // 30 second timeout
            })
            
            const response = await Promise.race([
                getPendingCases(mediatorExternalId),
                timeoutPromise
            ])

            // Check if response is valid
            if (response && response.success !== undefined) {
                if (response.success && Array.isArray(response.data)) {
                    // Filter cases with status 'awaiting_mediator_approval'
                    const pendingCases = response.data.filter(
                        (caseData) =>
                            caseData?.status === 'awaiting_mediator_approval'
                    )
                    setCount(pendingCases.length)
                } else {
                    // API call succeeded but no data or unsuccessful response
                    setCount(0)
                }
            } else {
                // Invalid response format
                console.warn('Invalid response format from getPendingCases:', response)
                setCount(0)
            }
        } catch (error) {
            // Only log non-timeout errors to avoid console spam
            // Timeout errors are expected on slow networks and don't need logging
            if (error?.message !== 'Request timeout') {
                console.error('Error fetching CAR cases badge count:', error)
            }
            // On API failure or timeout, set count to 0
            setCount(0)
        } finally {
            // Always set loading to false, even if there was an error
            setLoading(false)
        }
    }, [mediatorExternalId])

    useEffect(() => {
        fetchBadgeCount()
    }, [fetchBadgeCount])

    return {
        count,
        loading,
        refresh: fetchBadgeCount,
    }
}

export default useCarCasesBadge

